import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import {
  Activity,
  Award,
  BookOpen,
  Calendar,
  Clock,
  FileText,
  Filter,
  Globe,
  Star,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react'
import ScrollReveal from '@/components/ScrollReveal'

/**
 * Mockup estático com dados fictícios — não reflete métricas reais de nenhuma empresa.
 */

const KPI_CARDS = [
  { label: 'USUÁRIOS ATIVOS', value: '232', hint: '1.840 cadastrados · +58 em 7d', icon: Users },
  {
    label: 'CONCLUSÕES DE CURSO',
    value: '612',
    hint: '38.4% de taxa · 980 em andamento',
    icon: BookOpen,
  },
  { label: 'BADGES CONCEDIDAS', value: '3.184', hint: '891 usuários · 6.2 por user', icon: Award },
  {
    label: 'ENGAJAMENTO',
    value: '94%',
    hint: '7.320 eps · 6.910 quizzes · ★4.9',
    icon: TrendingUp,
  },
]

const MODULE_FUNNEL = [
  { name: 'Mentalidade Orientada à IA', started: 812, completed: 792, rate: 98, color: 'bg-orange-500' },
  { name: 'Engenharia de Prompt', started: 745, completed: 706, rate: 95, color: 'bg-emerald-500' },
  { name: 'Engenharia de Contexto', started: 690, completed: 634, rate: 92, color: 'bg-blue-500' },
]

const BADGE_DISTRIBUTION = [
  { name: 'Resposta Exata', value: 512, color: 'bg-orange-500' },
  { name: 'Maratona', value: 498, color: 'bg-emerald-500' },
  { name: 'Mentalidade Orientada à IA', value: 471, color: 'bg-blue-500' },
  { name: 'Engenharia de Prompt', value: 402, color: 'bg-purple-500' },
  { name: 'Domínio Total', value: 355, color: 'bg-red-500' },
]

const TOP_USERS = [
  { name: 'Marina Costa Almeida', email: 'marina.almeida@empresa.com', count: 34, photo: '/avatars/marina-almeida.jpg' },
  { name: 'Rafael Tavares Lima', email: 'rafael.lima@empresa.com', count: 31, photo: '/avatars/rafael-lima.jpg' },
  { name: 'Bianca Ferreira Souza', email: 'bianca.souza@empresa.com', count: 29, photo: '/avatars/bianca-souza.jpg' },
  { name: 'Diego Martins Rocha', email: 'diego.rocha@empresa.com', count: 27, photo: '/avatars/diego-rocha.jpg' },
]

const ACTIVITY_SERIES = [
  { label: 'Novos cadastros', color: 'hsl(18 89% 54%)', points: [18, 26, 22, 34, 30, 42, 36, 50, 40, 54, 46, 60] },
  { label: 'Episódios completos', color: '#10b981', points: [10, 16, 14, 22, 18, 28, 24, 34, 28, 38, 32, 44] },
  { label: 'Quizzes', color: '#3b82f6', points: [8, 12, 10, 18, 14, 24, 20, 30, 24, 34, 28, 40] },
  { label: 'Badges', color: '#a855f7', points: [4, 8, 6, 12, 10, 16, 12, 20, 16, 24, 18, 28] },
]

const HEATMAP_ROWS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const HEATMAP_INTENSITY = [
  [0, 1, 2, 0, 0, 1, 0, 0, 3, 2, 1, 0],
  [1, 0, 2, 3, 2, 1, 0, 2, 3, 4, 2, 1],
  [0, 2, 3, 2, 1, 0, 1, 3, 4, 3, 2, 0],
  [0, 1, 2, 3, 2, 1, 2, 3, 2, 1, 0, 1],
  [1, 0, 1, 2, 3, 2, 1, 0, 2, 3, 1, 0],
  [0, 1, 0, 2, 1, 0, 1, 2, 1, 0, 2, 1],
  [0, 0, 1, 0, 0, 0, 1, 0, 1, 0, 0, 0],
]
const HEAT_COLORS = ['bg-muted/30', 'bg-primary/25', 'bg-primary/50', 'bg-primary/75', 'bg-primary']

const WORKBOOKS = [
  { views: 4, downloads: 5, width: '100%' },
  { views: 4, downloads: 3, width: '86%' },
  { views: 3, downloads: 3, width: '74%' },
  { views: 3, downloads: 1, width: '68%' },
  { views: 2, downloads: 2, width: '58%' },
  { views: 2, downloads: 1, width: '46%' },
]

const MODULE_COMPLETION = [
  { name: 'Mentalidade Orientada à IA', course: 'Jornada IA', ratio: '792/812', rate: 98 },
  { name: 'Básico', course: 'AI Symphony', ratio: '58/61', rate: 95 },
  { name: 'Engenharia de Prompt', course: 'Jornada IA', ratio: '706/745', rate: 95 },
  { name: 'Engenharia de Contexto', course: 'Jornada IA', ratio: '634/690', rate: 92 },
]

const RATING_DISTRIBUTION = [
  { stars: 5, count: 190 },
  { stars: 4, count: 21 },
  { stars: 3, count: 3 },
  { stars: 2, count: 0 },
  { stars: 1, count: 0 },
]

const REVIEWS = [
  {
    photo: '/avatars/gustavo-teixeira.jpg',
    name: 'Gustavo Ramos Teixeira',
    stars: 5,
    date: '28/07/2026',
    text: 'Os workbooks ajudam muito a fixar o conteúdo, virou parte da minha rotina semanal.',
  },
  {
    photo: '/avatars/bruna-ferreira.jpg',
    name: 'Bruna Salgado Ferreira',
    stars: 5,
    date: '21/07/2026',
    text: 'Gostei muito da comunidade, trocar experiência com outras equipes acelera o aprendizado.',
  },
  {
    photo: '/avatars/larissa-prado.jpg',
    name: 'Larissa Machado Prado',
    stars: 4,
    date: '15/07/2026',
    text: 'Muito bom, só senti falta de mais exemplos práticos voltados pro meu setor.',
  },
  {
    photo: '/avatars/amanda-sato.jpg',
    name: 'Amanda Sato Kimura',
    stars: 5,
    date: '08/07/2026',
    text: 'Didática excelente, os vídeos curtos facilitam encaixar no dia a dia de trabalho.',
  },
  {
    photo: '/avatars/eduardo-carvalho.jpg',
    name: 'Eduardo Nunes Carvalho',
    stars: 5,
    date: '30/06/2026',
    text: 'Superou minhas expectativas, já recomendei o curso pra todo o meu time.',
  },
]

function DashboardChrome({
  title,
  subtitle,
  icon: Icon,
  children,
}: {
  title: string
  subtitle?: string
  icon: typeof Globe
  children: React.ReactNode
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-[#0a0a0c] p-5 sm:p-7 text-white h-full flex flex-col text-left">
      <div className={`flex items-center gap-2 ${subtitle ? 'mb-1' : 'mb-5'}`}>
        <Icon className="w-5 h-5 text-primary" />
        <span className="text-lg font-bold">{title}</span>
      </div>
      {subtitle && <p className="text-xs text-white/50 mb-6">{subtitle}</p>}
      <div className="flex-1 flex flex-col min-h-0">{children}</div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/90 to-transparent" />
    </div>
  )
}

function SlideOverview() {
  return (
    <DashboardChrome title="Super Admin" icon={Globe}>
      <div className="flex items-center gap-2 text-[11px] text-white/40 mb-4">
        <Filter className="w-3.5 h-3.5" />
        <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10">Todas as empresas</span>
        <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10">Jornada IA</span>
        <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10">Últimos 30 dias</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
        {KPI_CARDS.map((kpi) => (
          <div key={kpi.label} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] font-semibold tracking-wide text-white/40">{kpi.label}</span>
              <kpi.icon className="w-3.5 h-3.5 text-primary" />
            </div>
            <p className="text-xl font-bold">{kpi.value}</p>
            <p className="text-[9px] text-white/35 mt-0.5">{kpi.hint}</p>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5">
          <p className="text-xs font-semibold mb-3">Funil de módulos</p>
          <div className="space-y-2.5">
            {MODULE_FUNNEL.map((m) => (
              <div key={m.name}>
                <div className="flex justify-between text-[10px] text-white/50 mb-1">
                  <span className="truncate pr-2">{m.name}</span>
                  <span>{m.completed}/{m.started}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className={`h-full rounded-full ${m.color}`} style={{ width: `${m.rate}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5">
          <p className="text-xs font-semibold mb-3">Distribuição de badges</p>
          <div className="space-y-2">
            {BADGE_DISTRIBUTION.map((b) => (
              <div key={b.name} className="flex items-center gap-2 text-[10px] text-white/50">
                <span className="w-28 truncate">{b.name}</span>
                <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${b.color}`}
                    style={{ width: `${(b.value / BADGE_DISTRIBUTION[0].value) * 100}%` }}
                  />
                </div>
                <span className="w-8 text-right text-white/40">{b.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 max-h-[130px] overflow-hidden rounded-lg">
        <ContentPerformanceGrid />
      </div>
    </DashboardChrome>
  )
}

function SlineChartMock() {
  const maxValue = 60
  return (
    <svg viewBox="0 0 280 70" className="w-full h-full" preserveAspectRatio="none">
      {ACTIVITY_SERIES.map((series) => {
        const points = series.points
          .map((v, i) => {
            const x = (i / (series.points.length - 1)) * 280
            const y = 70 - (v / maxValue) * 60
            return `${x},${y}`
          })
          .join(' ')
        return <polyline key={series.label} points={points} fill="none" stroke={series.color} strokeWidth="2" />
      })}
    </svg>
  )
}

function SlineChart() {
  return (
    <div className="flex-1 min-h-[6rem]">
      <SlineChartMock />
    </div>
  )
}

function SlideActivity() {
  return (
    <DashboardChrome title="Atividade ao longo do tempo" subtitle="Novos cadastros · episódios completos · quizzes · badges" icon={Activity}>
      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5 mb-3 flex-1 flex flex-col min-h-0">
        <SlineChart />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-white/40 mt-2">
          {ACTIVITY_SERIES.map((series) => (
            <span key={series.label} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: series.color }} />
              {series.label}
            </span>
          ))}
          <span className="ml-auto text-emerald-400 font-medium">+42% no período</span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5">
          <p className="text-xs font-semibold mb-1">Mapa de atividade</p>
          <p className="text-[9px] text-white/35 mb-2">312 episódios completos · últimos 90d</p>
          <div className="space-y-[3px]">
            {HEATMAP_ROWS.map((day, r) => (
              <div key={day} className="flex items-center gap-1">
                <span className="w-6 text-[8px] text-white/30">{day}</span>
                <div className="flex gap-[3px] flex-1">
                  {HEATMAP_INTENSITY[r].map((intensity, c) => (
                    <div key={c} className={`h-2.5 flex-1 rounded-sm ${HEAT_COLORS[intensity]}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5">
          <div className="flex items-center gap-1.5 mb-3">
            <Trophy className="w-3.5 h-3.5 text-primary" />
            <p className="text-xs font-semibold">Top usuários</p>
          </div>
          <div className="space-y-2.5">
            {TOP_USERS.map((u, i) => (
              <div key={u.email} className="flex items-center gap-2">
                <span className="text-[10px] text-white/30 w-3">{i + 1}</span>
                <img
                  src={u.photo}
                  alt={u.name}
                  className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-medium truncate">{u.name}</p>
                </div>
                <span className="text-[10px] font-semibold text-primary">{u.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardChrome>
  )
}

function CompletionTimeCard() {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5 text-center">
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <Clock className="w-3.5 h-3.5 text-primary" />
        <p className="text-[10px] font-semibold">Tempo de conclusão</p>
      </div>
      <p className="text-3xl font-bold my-2">6</p>
      <p className="text-[9px] text-white/40 mb-3">DIAS EM MÉDIA</p>
      <div className="flex justify-between text-[9px] text-white/40 border-t border-white/10 pt-2">
        <span>Mais rápido<br /><span className="text-emerald-400 font-semibold">1d</span></span>
        <span>Mais lento<br /><span className="text-white/60 font-semibold">22d</span></span>
      </div>
    </div>
  )
}

function WorkbooksCard() {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5">
      <div className="flex items-center gap-1.5 mb-3">
        <FileText className="w-3.5 h-3.5 text-primary" />
        <p className="text-[10px] font-semibold">Workbooks mais acessados</p>
      </div>
      <div className="space-y-1.5">
        {WORKBOOKS.map((w, i) => (
          <div key={i} className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: w.width }} />
          </div>
        ))}
      </div>
    </div>
  )
}

function ModuleCompletionCard() {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3.5">
      <div className="flex items-center gap-1.5 mb-3">
        <BookOpen className="w-3.5 h-3.5 text-primary" />
        <p className="text-[10px] font-semibold">Conclusão por módulo</p>
      </div>
      <div className="space-y-2.5">
        {MODULE_COMPLETION.map((m) => (
          <div key={m.name}>
            <div className="flex justify-between text-[9px] text-white/50 mb-1">
              <span className="truncate pr-2">{m.name}</span>
              <span>{m.rate}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-primary" style={{ width: `${m.rate}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ContentPerformanceGrid() {
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <CompletionTimeCard />
      <WorkbooksCard />
      <ModuleCompletionCard />
    </div>
  )
}

function SlideReviews() {
  const maxCount = RATING_DISTRIBUTION[0].count
  return (
    <DashboardChrome title="Avaliações dos cursos" subtitle="Média 4.9 ★ · 214 avaliações" icon={Star}>
      <div className="grid sm:grid-cols-[minmax(0,220px)_1fr] gap-4">
        <div className="space-y-1.5">
          {RATING_DISTRIBUTION.map((r) => (
            <div key={r.stars} className="flex items-center gap-2 text-[10px] text-white/50">
              <span className="w-4">{r.stars}★</span>
              <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-amber-500 to-primary"
                  style={{ width: `${(r.count / maxCount) * 100}%` }}
                />
              </div>
              <span className="w-6 text-right text-white/40">{r.count}</span>
            </div>
          ))}
          <div className="pt-2">
            <CompletionTimeCard />
          </div>
        </div>
        <div className="space-y-3">
          {REVIEWS.map((review) => (
            <div key={review.name} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <img
                  src={review.photo}
                  alt={review.name}
                  className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                />
                <p className="text-[10px] font-semibold flex-1 truncate">{review.name}</p>
                <div className="flex text-amber-400">
                  {Array.from({ length: review.stars }).map((_, i) => (
                    <Star key={i} className="w-2.5 h-2.5 fill-amber-400" />
                  ))}
                </div>
                <span className="text-[8px] text-white/30 flex items-center gap-0.5">
                  <Calendar className="w-2.5 h-2.5" /> {review.date}
                </span>
              </div>
              <p className="text-[10px] text-white/50 leading-relaxed">{review.text}</p>
            </div>
          ))}
        </div>
      </div>
    </DashboardChrome>
  )
}

const CARD_WIDTH = 640
const CARD_HEIGHT = 480

function FanCards() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] })

  const xLeft = useTransform(scrollYProgress, [0, 0.85], [0, -(CARD_WIDTH / 2)])
  const xRight = useTransform(scrollYProgress, [0, 0.85], [0, CARD_WIDTH / 2])
  const rotateLeft = useTransform(scrollYProgress, [0, 0.85], [0, -6])
  const rotateRight = useTransform(scrollYProgress, [0, 0.85], [0, 6])
  const sideOpacity = useTransform(scrollYProgress, [0, 0.5], [0, 1])
  const sideScale = useTransform(scrollYProgress, [0, 0.85], [0.82, 1])
  const centerScale = useTransform(scrollYProgress, [0, 0.85], [1, 0.96])

  const cardBaseStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    marginLeft: -CARD_WIDTH / 2,
    marginTop: -CARD_HEIGHT / 2,
  }

  return (
    <div ref={sectionRef} className="hidden md:block relative overflow-x-clip" style={{ height: '150vh' }}>
      <div className="sticky top-0 h-screen flex flex-col items-center justify-center">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground">
            E você acompanha o progresso das suas equipes de qualquer lugar.
          </h3>
        </div>
        <div className="relative" style={{ height: CARD_HEIGHT }}>
          <motion.div
            style={{ ...cardBaseStyle, x: xLeft, rotate: rotateLeft, opacity: sideOpacity, scale: sideScale, zIndex: 10 }}
          >
            <SlideActivity />
          </motion.div>
          <motion.div style={{ ...cardBaseStyle, scale: centerScale, zIndex: 30 }}>
            <SlideOverview />
          </motion.div>
          <motion.div
            style={{ ...cardBaseStyle, x: xRight, rotate: rotateRight, opacity: sideOpacity, scale: sideScale, zIndex: 10 }}
          >
            <SlideReviews />
          </motion.div>
        </div>
      </div>
    </div>
  )
}

function StackedCards() {
  return (
    <div className="md:hidden space-y-6">
      <ScrollReveal>
        <div className="max-w-3xl mx-auto text-center mb-2">
          <h3 className="text-2xl font-bold text-foreground">
            E você acompanha o progresso das suas equipes de qualquer lugar.
          </h3>
        </div>
      </ScrollReveal>
      <ScrollReveal>
        <SlideOverview />
      </ScrollReveal>
      <ScrollReveal delay={0.1}>
        <SlideActivity />
      </ScrollReveal>
      <ScrollReveal delay={0.2}>
        <SlideReviews />
      </ScrollReveal>
    </div>
  )
}

export default function SuperAdminShowcase() {
  return (
    <div>
      <FanCards />
      <StackedCards />
    </div>
  )
}
